import { Button, useDisclosure } from '@chakra-ui/react'
import type { AxiosError } from 'axios'
import SubmitDialog from 'BrokerTsLib/src/components/SubmitDialog'
import type { Addon } from 'BrokerTsLib/src/dtos'
import type { DiscountBase, StepBase } from 'BrokerTsLib/src/dtos/StepBase'
import { FormProvider, useForm, yupResolver } from 'BrokerTsLib/src/rhf'
import { IndividualDiscountsSchema } from 'BrokerTsLib/src/validation'
import { useContext, useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import * as yup from 'yup'

import ErrorSummary from '../../components/ErrorSummary'
import {
  FormSection,
  FormSectionBody,
  FormSectionDescription,
  FormSectionHeader,
  FormSectionTitle,
} from '../../components/FormSection'
import { FormSpacer } from '../../components/FormSpacer'
import { Loading } from '../../components/Loading'
import { MainContent } from '../../components/MainContent'
import { useGetAddons, useSaveStep2, useUpdateLastWizardStep } from '../../hooks/useWizard'
import { Save } from '../../icons'
import { insuranceParamsContext } from '../../layouts/Wizard'
import { Discounts } from '../discounts/Discounts'
import { ExtraInsuranceCar } from '../ExtraInsuranceCar'
import { FormButtons } from '../FormButtons'


export type Step2FormValues = {
  paramsId: string
  addons: Addon[]
} & StepBase & DiscountBase

const schema = yup.object().shape(
  {
    individualDiscounts: IndividualDiscountsSchema
  }
)

export const Step2Form = () => {
  const navigate = useNavigate()
  const { insuranceParams, insuranceParamsId: paramsId, refetchInsuranceParams } = useContext(insuranceParamsContext)!
  const isContracted = !!insuranceParams?.generalParams.contractNumber
  const [isSubmittingCurrent, setIsSubmittingCurrent] = useState(false)
  const [isSubmittingNext, setIsSubmittingNext] = useState(false)
  const [discountsLoaded, setDiscountsLoaded] = useState(false)

  const methods = useForm<Step2FormValues>({
    defaultValues: {},
    resolver: yupResolver(schema) as any,
    mode: 'onBlur'
  })

  const {
    handleSubmit,
    getValues,
    setValue,
    setError,
    formState: { errors, isSubmitting },
  } = methods

  useEffect(() => {
    if (!isSubmitting) {
      setIsSubmittingCurrent(false)
      setIsSubmittingNext(false)
    }
  }, [isSubmitting])

  const { data, isLoading } = useGetAddons(paramsId ?? '', {
    onSuccess: ({ data }) => { setValue('addons', data) }
  })

  const { mutateAsync: updateStep } = useUpdateLastWizardStep()
  const { mutateAsync: submitForm } = useSaveStep2()

  const handlePostError = (data: any) => {
    Object.keys(data).forEach((x: any) => {
      if (x.toString().toLowerCase().startsWith('discounts[')) {
        setError(x.toLowerCase(), { message: data[x].join(' ') })
      }
    })
  }

  const {
    isOpen: isSavedFormAlertOpen,
    onOpen: savedFormAlertOnOpen,
    onClose: savedFormAlertOnClose,
  } = useDisclosure()

  const onSubmitForm = handleSubmit((data) => {
    return submitForm({ ...data, paramsId: paramsId ?? '' })
      .then((response) => {
        if (data.nextPage == 'next') {
          if ((insuranceParams?.generalParams.lastWizardStep ?? 2) < 3) {
            updateStep({ paramsId: Number(paramsId), step: 3, stepsStep: 'carkulka' }).then(() => {
              refetchInsuranceParams().then(() => {
                navigate(`/carkulka/wizard/3/${paramsId}`)
              })
            })
          }
          else {
            navigate(`/carkulka/wizard/3/${paramsId}`)
          }
        }
        else {
          savedFormAlertOnOpen()
        }
      })
      .catch((err: AxiosError) => {
        if (!(err.response && err.response.status === 400 && err.response.data))
          return

        const data = err.response.data
        handlePostError(data)
      })
      .finally(() => { })
  })

  const setAddon = (addon: Addon) => {
    const addons = getValues('addons') ?? []
    const index = addons.findIndex(x => x.riskId === addon.riskId)
    if (index < 0) addons.push(addon)
    else addons[index] = addon
    setValue('addons', addons)
  }

  return (
    <FormProvider {...methods}>
      <form onSubmit={onSubmitForm}>
        <MainContent>
          <FormSection>
            <FormSectionHeader>
              <FormSectionTitle>Připojištění</FormSectionTitle>

              <FormSectionDescription>
                Vyberte limity zvoleného připojišťění.
              </FormSectionDescription>
            </FormSectionHeader>

            {isLoading && <Loading large={true} />}
            {!isLoading &&
              <FormSectionBody>
                <ExtraInsuranceCar addons={data?.data ?? []} changeAddon={setAddon} />
              </FormSectionBody>
            }
          </FormSection>

          <FormSection>
            <FormSectionHeader>
              <FormSectionTitle>Slevy pojištění</FormSectionTitle>

              <FormSectionDescription>
                Nastavte slevy pojistníka a jednotlivých pojišťoven.
              </FormSectionDescription>
            </FormSectionHeader>

            <FormSectionBody>
              <Discounts onDiscountsLoaded={() => setDiscountsLoaded(true)} />
            </FormSectionBody>
          </FormSection>

          {!isContracted &&
            <FormButtons
              rightButtons={
                <>
                  <Button variant="outline"
                    isDisabled={isSubmitting || isSubmittingNext || isSubmittingCurrent || !discountsLoaded}
                    isLoading={isSubmittingCurrent}
                    loadingText="Ukládám..."
                    type="button"
                    onClick={() => {
                      setValue('nextPage', 'current')
                      setIsSubmittingCurrent(true)
                      onSubmitForm()
                    }}>
                    Uložit
                  </Button>

                  <Button
                    colorScheme="primary"
                    isLoading={isSubmittingNext}
                    isDisabled={isSubmitting || isSubmittingNext || isSubmittingCurrent || !discountsLoaded}
                    loadingText="Počítám nabídky..."
                    type="button"
                    onClick={() => {
                      setValue('nextPage', 'next')
                      setIsSubmittingNext(true)
                      onSubmitForm()
                    }}>
                    <Save />
                    Uložit a spočítat nabídky
                  </Button>

                  <SubmitDialog
                    isOpen={isSavedFormAlertOpen}
                    onClose={savedFormAlertOnClose}
                    header='Uloženo'
                    body={'Parametry nabídky byly úspěšně uloženy'}
                  />
                </>
              }
            />}
        </MainContent>

        <FormSpacer />

        <ErrorSummary errors={errors} />
      </form>
    </FormProvider>
  )
}
